import { ApiResponse } from '@shared/types';
import { SaveTaxRateRequest, TaxRateRecord } from '../types/accounting';
import { supabase } from './supabase';

const toNumber = (value: unknown): number => {
  if (typeof value === 'number') return value;
  return Number(value || 0);
};

const mapTaxRate = (value: Record<string, unknown>): TaxRateRecord => ({
  id: String(value.id || ''),
  company_id: toNumber(value.company_id),
  code: String(value.code || ''),
  name: String(value.name || ''),
  rate: toNumber(value.rate),
  application: value.application as TaxRateRecord['application'],
  status: value.status as TaxRateRecord['status'],
  output_account_id: (value.output_account_id as string | null) ?? null,
  input_account_id: (value.input_account_id as string | null) ?? null,
  description: (value.description as string | null) ?? null,
  created_by: (value.created_by as string | null) ?? null,
  created_at: String(value.created_at || ''),
  updated_at: String(value.updated_at || ''),
});

export const fetchTaxRates = async (
  companyId: number,
): Promise<ApiResponse<TaxRateRecord[]>> => {
  try {
    const { data, error } = await supabase
      .from('tax_rates')
      .select('*')
      .eq('company_id', companyId)
      .order('name', { ascending: true });

    if (error) {
      return { success: false, error: error.message, data: [] };
    }

    return {
      success: true,
      data: (data || []).map((item) => mapTaxRate(item)),
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to fetch tax rates',
      data: [],
    };
  }
};

export const saveTaxRate = async (
  input: SaveTaxRateRequest,
): Promise<ApiResponse<TaxRateRecord>> => {
  const payload = {
    company_id: input.companyId,
    code: input.code.trim(),
    name: input.name.trim(),
    rate: input.rate,
    application: input.application,
    status: input.status,
    output_account_id: input.outputAccountId ?? null,
    input_account_id: input.inputAccountId ?? null,
    description: input.description?.trim() || null,
  };

  try {
    if (input.id) {
      const { data, error } = await supabase
        .from('tax_rates')
        .update(payload)
        .eq('company_id', input.companyId)
        .eq('id', input.id)
        .select('*')
        .single();

      if (error) {
        return { success: false, error: error.message, data: undefined };
      }

      return { success: true, data: mapTaxRate(data) };
    }

    const { data, error } = await supabase
      .from('tax_rates')
      .insert(payload)
      .select('*')
      .single();

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    return { success: true, data: mapTaxRate(data) };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to save tax rate',
      data: undefined,
    };
  }
};

export const deleteTaxRate = async (
  companyId: number,
  taxRateId: string,
): Promise<ApiResponse<null>> => {
  try {
    const { error } = await supabase
      .from('tax_rates')
      .delete()
      .eq('company_id', companyId)
      .eq('id', taxRateId);

    if (error) {
      return { success: false, error: error.message, data: null };
    }

    return { success: true, data: null };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to delete tax rate',
      data: null,
    };
  }
};
